import React from 'react';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export const formatRating = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

export const StarRating = ({
  value = 0,
  sizeClassName = 'text-base',
  className = '',
  showValue = false,
  valueClassName = 'text-gray-600',
  max = 5,
  ariaLabel,
}) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? clamp(n, 0, max) : 0;

  return (
    <div
      className={`inline-flex items-center gap-1 ${className}`}
      aria-label={ariaLabel || `Rating ${formatRating(safe)} out of ${max}`}
    >
      {Array.from({ length: max }).map((_, i) => {
        const fill = clamp(safe - i, 0, 1);
        return (
          <span key={i} className={`relative inline-block leading-none ${sizeClassName}`} aria-hidden="true">
            <span className="text-gray-300">★</span>
            <span
              className="absolute left-0 top-0 overflow-hidden text-discovery-gold"
              style={{ width: `${fill * 100}%` }}
            >
              ★
            </span>
          </span>
        );
      })}
      {showValue && <span className={valueClassName}>{formatRating(safe)}/{max}</span>}
    </div>
  );
};

export const StarRatingInput = ({
  value,
  onChange,
  disabled = false,
  sizeClassName = 'text-2xl',
  className = '',
  max = 5,
  min = 1,
  step = 0.5,
  /**
   * Fraction of the button width that counts as the "left half".
   * Bumping this slightly above 0.5 makes half-star selection less finicky,
   * especially because the star glyph is visually centered within padded buttons.
   */
  halfClickThreshold = 0.6,
  ariaLabel = 'Rating',
}) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? clamp(n, min, max) : max;

  const StarVisual = ({ fill }) => (
    <span className={`relative inline-block leading-none ${sizeClassName}`} aria-hidden="true">
      <span className="text-gray-300">★</span>
      <span
        className="absolute left-0 top-0 overflow-hidden text-discovery-gold"
        style={{ width: `${fill * 100}%` }}
      >
        ★
      </span>
    </span>
  );

  const applyStep = (next) => {
    const snapped = Math.round(next / step) * step;
    return clamp(snapped, min, max);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(applyStep(safe - step));
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(applyStep(safe + step));
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(min);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(max);
    }
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} role="group" aria-label={ariaLabel}>
      <div className="inline-flex items-center gap-1">
        {Array.from({ length: max }).map((_, i) => {
          const starNumber = i + 1;
          const fill = clamp(safe - i, 0, 1);
          const leftValue = applyStep(starNumber - 0.5);
          const rightValue = applyStep(starNumber);
          return (
            <button
              key={starNumber}
              type="button"
              disabled={disabled}
              onKeyDown={handleKeyDown}
              onClick={(e) => {
                if (disabled) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const threshold = clamp(Number(halfClickThreshold) || 0.6, 0.5, 0.9);
                const isLeftHalf = x < rect.width * threshold;
                onChange(isLeftHalf ? leftValue : rightValue);
              }}
              className="p-1 touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-passion-red rounded"
              aria-label={
                leftValue === rightValue
                  ? `Set rating to ${formatRating(rightValue)} stars`
                  : `Set rating to ${formatRating(leftValue)} or ${formatRating(rightValue)} stars`
              }
            >
              <StarVisual fill={fill} />
            </button>
          );
        })}
      </div>
      <span className="text-gray-600 text-sm sm:text-base">{formatRating(safe)}/{max}</span>
    </div>
  );
};

